import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api/meeting',
  timeout: 30000
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data
    if (res.code !== 1) {
      Message({
        message: res.msg || '请求失败',
        type: 'error',
        duration: 3000
      })
      // 401: Token失效
      if (res.code === 401) {
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        router.push('/login')
      }
      return Promise.reject(new Error(res.msg || '请求失败'))
    }
    return res
  },
  error => {
    console.error('响应错误:', error)
    Message({
      message: error.message || '网络错误',
      type: 'error',
      duration: 3000
    })
    return Promise.reject(error)
  }
)

// ==================== 用户相关API ====================
export const userApi = {
  // 用户登录
  login(data) {
    return service.post('/user/login', data)
  },
  // 用户注册
  register(data) {
    return service.post('/user/register', data)
  },
  // 获取用户信息
  getUserInfo() {
    return service.get('/user/info')
  },
  // 更新用户信息
  updateUserInfo(data) {
    return service.post('/user/update', data)
  },
  // 退出登录
  logout() {
    return service.post('/user/logout')
  },
  // 修改密码
  changePassword(data) {
    return service.post('/user/changePassword', data)
  }
}

// ==================== 会议相关API ====================
export const conferenceApi = {
  // 获取会议详情
  getDetail(id = 1) {
    return service.get('/conference/detail', { params: { id } })
  },
  // 获取会议列表
  getList(params) {
    return service.get('/conference/list', { params })
  },
  // 获取Banner列表
  getBanners(conferenceId = 1) {
    return service.get('/conference/banners', { params: { conference_id: conferenceId } })
  }
}

// ==================== 日程相关API ====================
export const scheduleApi = {
  // 获取日程列表
  getList(conferenceId = 1) {
    return service.get('/schedule/list', { params: { conference_id: conferenceId } })
  },
  // 获取简要日程
  getBrief(conferenceId = 1) {
    return service.get('/schedule/brief', { params: { conference_id: conferenceId } })
  }
}

// ==================== 摘要相关API ====================
export const paperApi = {
  // 提交摘要
  submit(data) {
    return service.post('/paper/submit', data)
  },
  // 获取我的摘要列表
  getMyPapers(params) {
    return service.get('/paper/my', { params })
  },
  // 获取公开摘要列表
  getPublicPapers(params) {
    return service.get('/paper/list', { params })
  },
  // 下载摘要
  download(id) {
    return service.get('/paper/download', { params: { id } })
  }
}

// ==================== 参会报名相关API ====================
export const registrationApi = {
  // 报名参会
  submit(data) {
    return service.post('/registration/submit', data)
  },
  // 获取我的报名记录
  getMyRegistrations(params) {
    return service.get('/registration/my', { params })
  },
  // 获取报名详情
  getDetail(id) {
    return service.get('/registration/detail', { params: { id } })
  },
  // 创建支付订单
  createPayment(registrationId) {
    return service.post('/registration/createPayment', { registration_id: registrationId })
  },
  // 查询支付状态
  queryPayStatus(orderNo) {
    return service.get('/registration/queryPayStatus', { params: { order_no: orderNo } })
  }
}

// ==================== 参会须知相关API ====================
export const noticeApi = {
  // 获取须知列表
  getList(conferenceId = 1) {
    return service.get('/notice/list', { params: { conference_id: conferenceId } })
  },
  // 获取须知详情
  getDetail(id) {
    return service.get('/notice/detail', { params: { id } })
  }
}

// ==================== 会场信息相关API ====================
export const venueApi = {
  // 获取会场列表
  getList() {
    return service.get('/venue/list')
  },
  // 获取会场详情
  getDetail(id) {
    return service.get('/venue/detail', { params: { id } })
  }
}

// ==================== 酒店信息相关API ====================
export const hotelApi = {
  // 获取酒店列表
  getList() {
    return service.get('/hotel/list')
  },
  // 获取酒店详情
  getDetail(id) {
    return service.get('/hotel/detail', { params: { id } })
  }
}

// ==================== 景点指南相关API ====================
export const attractionApi = {
  // 获取景点列表
  getList(category = '') {
    return service.get('/attraction/list', { params: { category } })
  },
  // 获取景点详情
  getDetail(id) {
    return service.get('/attraction/detail', { params: { id } })
  }
}

// ==================== 参会者手册相关API ====================
export const handbookApi = {
  // 获取手册列表
  getList(category = '') {
    return service.get('/handbook/list', { params: { category } })
  },
  // 获取手册详情
  getDetail(id) {
    return service.get('/handbook/detail', { params: { id } })
  }
}

// ==================== 演讲者须知相关API ====================
export const speakerGuideApi = {
  // 获取演讲者须知列表
  getList(category = '') {
    return service.get('/speaker_guide/list', { params: { category } })
  },
  // 获取演讲者须知详情
  getDetail(id) {
    return service.get('/speaker_guide/detail', { params: { id } })
  }
}

// ==================== 照片画廊相关API ====================
export const galleryApi = {
  // 获取照片分类
  getCategories(conferenceId = 1) {
    return service.get('/gallery/categories', { params: { conference_id: conferenceId } })
  },
  // 获取照片列表
  getList(params) {
    return service.get('/gallery/list', { params })
  }
}

// ==================== 联系我们相关API ====================
export const contactApi = {
  // 获取联系信息
  getList(conferenceId = 1) {
    return service.get('/contact/list', { params: { conference_id: conferenceId } })
  }
}

// ==================== 文件上传API ====================
export const uploadApi = {
  // 上传文件
  upload(file, onProgress) {
    const formData = new FormData()
    formData.append('file', file)
    return service.post('/common/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: progressEvent => {
        if (onProgress) {
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100)
          onProgress(percent)
        }
      }
    })
  }
}

export default service
